import { useState } from 'react';
import { ToastContainer } from 'react-toastify';

import AppContent from 'src/shared/components/AppContent';
import ConfirmProvider from 'src/shared/components/ui/ConfirmProvider';
import { useIsPhone } from 'src/shared/hooks/useMediaQuery';
import useQuickAction from 'src/shared/hooks/useQuickAction';
import type { QuickAction } from 'src/shared/hooks/useQuickAction';
import AccountSheet from './AccountSheet';
import BottomNav from './BottomNav';
import CreateSheet from './CreateSheet';
import QuickActionHost from './QuickActionHost';
import SideNav from './SideNav';
import TopBar from './TopBar';
import { useNavItems } from './navItems';

type Sheet = 'create' | 'more' | 'account' | null;

/**
 * The signed-in app: navigation around the routed page, plus the dialogs any
 * page can open (quick actions, confirmations, toasts).
 */
export default function AppShell() {
  const items = useNavItems();
  const isPhone = useIsPhone();
  const quickAction = useQuickAction();
  const [sheet, setSheet] = useState<Sheet>(null);

  const closeSheet = () => setSheet(null);

  const choose = (action: QuickAction) => {
    setSheet(null);
    quickAction.open(action);
  };

  // A link, not an anchor: `#main-content` would be read as a route by the
  // hash router and navigate away.
  const skipToContent = (event: React.MouseEvent) => {
    event.preventDefault();
    document.getElementById('main-content')?.focus();
  };

  return (
    <ConfirmProvider>
      <a className="skip-link" href="#main-content" onClick={skipToContent}>
        Saltar al contenido principal
      </a>

      <div className="v-shell">
        <SideNav
          items={items}
          onNewAppointment={() => quickAction.open('nueva-cita')}
          onOpenAccount={() => setSheet('account')}
        />

        <div className="v-shell__body">
          <TopBar onOpenAccount={() => setSheet('account')} />
          <main id="main-content" className="v-main" tabIndex={-1}>
            <AppContent />
          </main>
        </div>

        <BottomNav
          items={items}
          onCreate={() => setSheet('create')}
          onMore={() => setSheet('more')}
        />
      </div>

      <CreateSheet isOpen={sheet === 'create'} toggle={closeSheet} onChoose={choose} />
      <AccountSheet
        isOpen={sheet === 'more'}
        toggle={closeSheet}
        title="Más"
        items={items.filter((item) => item.placement === 'more')}
      />
      <AccountSheet isOpen={sheet === 'account'} toggle={closeSheet} title="Tu cuenta" />

      <QuickActionHost />

      <ToastContainer
        position={isPhone ? 'bottom-center' : 'top-right'}
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick={false}
        pauseOnFocusLoss={false}
        theme="light"
      />
    </ConfirmProvider>
  );
}
